import { type Evaluated, evaluate, parentsOf } from '@/domain/construction';
import { type EntityId, type SceneDocument, topologicalOrder } from '@/domain/model';

import { buildIndex, EMPTY_INDEX, type SpatialIndex, updateIndex } from './spatialIndex';

/**
 * The evaluated scene.
 *
 * Holds the value of every entity, recomputed from the document's construction DAG. The
 * document is the source of truth; this is a cache that is always derivable from it —
 * which is why nothing here is ever persisted, and why loading an old file under a newer
 * evaluator produces corrected geometry rather than stale coordinates.
 *
 * Two evaluation paths:
 *
 * - `evaluateAll` — a full topological sweep. Used on load and after structural edits.
 * - `evaluateFrom` — dirties only the transitive dependents of what changed and
 *   re-evaluates those in topological order. This is the drag path, and it is the reason
 *   dragging one point in a 5 000-entity diagram costs the size of its subtree rather
 *   than the size of the scene.
 */

export interface Scene {
  readonly document: SceneDocument;
  readonly values: ReadonlyMap<EntityId, Evaluated | null>;
  /**
   * Topological order used for the last evaluation, or `null` when the document
   * contains a cycle and could not be ordered.
   */
  readonly order: readonly EntityId[] | null;
  /**
   * Where everything is, for picking and culling.
   *
   * Carried on the scene rather than rebuilt per query because it must be *incremental*: a
   * drag re-evaluates a subtree on every pointer move, and rebuilding a whole index each time
   * would cost more than the linear scan it replaces.
   */
  readonly index: SpatialIndex;
}

/** Entities whose value changed in an incremental update. */
export interface EvaluationResult {
  readonly scene: Scene;
  readonly changed: ReadonlySet<EntityId>;
}

/**
 * Evaluates the whole document.
 *
 * A cyclic document yields an empty value map rather than a partial one: a cycle means
 * the construction is not well-founded, and showing half of it would be more confusing
 * than showing none. The editor rejects cycles at construction time (`wouldCreateCycle`),
 * so reaching this state means a hand-edited or corrupt file.
 */
export function evaluateAll(document: SceneDocument): Scene {
  const order = topologicalOrder(document);
  if (order === null) {
    return { document, values: new Map(), order: null, index: EMPTY_INDEX };
  }

  const values = new Map<EntityId, Evaluated | null>();
  for (const id of order) {
    values.set(id, evaluateOne(document, values, id));
  }
  return { document, values, order, index: buildIndex(values) };
}

/** Creates an empty scene for an empty document. */
export function createScene(document: SceneDocument): Scene {
  return evaluateAll(document);
}

export function valueOf(scene: Scene, id: EntityId): Evaluated | null {
  return scene.values.get(id) ?? null;
}

/**
 * Re-evaluates only what `seeds` can affect.
 *
 * The dirty set is the transitive closure of `seeds` over the child edges. Walking it in
 * the scene's existing topological order guarantees each node is visited after its
 * parents without needing a fresh sort — a full re-sort on every pointer move would
 * undo the point of the incremental path.
 *
 * `changed` reports which values actually differ, so the renderer can invalidate exactly
 * those bounds. An entity that was re-evaluated to the same value is not reported:
 * dragging a point in a way that leaves a dependent circle unchanged should not repaint
 * the circle.
 */
export function evaluateFrom(
  scene: Scene,
  document: SceneDocument,
  seeds: Iterable<EntityId>
): EvaluationResult {
  // A structural change invalidates the cached order; fall back to a full sweep.
  if (scene.order === null || document.entities.size !== scene.document.entities.size) {
    return diffAgainst(scene, evaluateAll(document));
  }

  const children = buildChildIndex(document);
  const dirty = new Set<EntityId>();
  const queue: EntityId[] = [];

  for (const seed of seeds) {
    if (!document.entities.has(seed) || dirty.has(seed)) continue;
    dirty.add(seed);
    queue.push(seed);
  }

  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined) break;
    for (const child of children.get(current) ?? []) {
      if (dirty.has(child)) continue;
      dirty.add(child);
      queue.push(child);
    }
  }

  const values = new Map(scene.values);
  const changed = new Set<EntityId>();

  for (const id of scene.order) {
    if (!dirty.has(id)) continue;
    const next = evaluateOne(document, values, id);
    if (!sameValue(values.get(id) ?? null, next)) changed.add(id);
    values.set(id, next);
  }

  return {
    scene: {
      document,
      values,
      order: scene.order,
      // Only what changed is re-placed; the rest of the index is shared with the old scene.
      index: updateIndex(scene.index, values, changed),
    },
    changed,
  };
}

/* --------------------------------------------------------------------- internals */

function evaluateOne(
  document: SceneDocument,
  values: ReadonlyMap<EntityId, Evaluated | null>,
  id: EntityId
): Evaluated | null {
  const entity = document.entities.get(id);
  if (entity === undefined) return null;

  const parents = parentsOf(entity.source).map((parentId) => values.get(parentId) ?? null);
  return evaluate(entity.source, parents);
}

function buildChildIndex(document: SceneDocument): ReadonlyMap<EntityId, EntityId[]> {
  const children = new Map<EntityId, EntityId[]>();
  for (const entity of document.entities.values()) {
    for (const parent of parentsOf(entity.source)) {
      const list = children.get(parent);
      if (list === undefined) {
        children.set(parent, [entity.id]);
      } else {
        list.push(entity.id);
      }
    }
  }
  return children;
}

function diffAgainst(previous: Scene, next: Scene): EvaluationResult {
  const changed = new Set<EntityId>();
  for (const [id, value] of next.values) {
    if (!sameValue(previous.values.get(id) ?? null, value)) changed.add(id);
  }
  for (const id of previous.values.keys()) {
    if (!next.values.has(id)) changed.add(id);
  }
  return { scene: next, changed };
}

/**
 * Structural equality of two evaluated values.
 *
 * Compared field by field rather than with `JSON.stringify`, which would allocate two
 * strings per entity per frame — an easy way to turn the incremental path back into the
 * thing it was meant to avoid.
 */
function sameValue(a: Evaluated | null, b: Evaluated | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (a.kind !== b.kind) return false;

  switch (a.kind) {
    /*
     * Compared by the *formula and range*, not by the sampled points.
     *
     * The points are a pure function of those two, so comparing them would walk hundreds of
     * coordinates to reach an answer two string comparisons already have — on a path that runs
     * for every entity on every pointer move.
     */
    /*
     * Compared by the two axis ends, which pin down the scale and the extent between them.
     * Walking a few hundred tick segments to reach an answer four points already have would
     * be work done on every entity on every pointer move.
     */
    case 'axes':
      return (
        b.kind === 'axes' &&
        samePoint(a.xAxis.b, b.xAxis.b) &&
        samePoint(a.yAxis.b, b.yAxis.b) &&
        a.ticks.length === b.ticks.length &&
        a.marks.length === b.marks.length
      );

    /*
     * Compared by its corner and its shape, not by its hundreds of rules and numbers.
     *
     * The contents are a pure function of the formula, so two tables with the same corner and
     * the same number of cells are the same table — and the formula changing always changes
     * the count or a number, both of which show up in `marks`.
     */
    /*
     * Compared on the dots and the text, which is everything a repaint can turn on.
     *
     * The guides are determined by the dots and the axes, so two values agreeing on the points
     * and disagreeing on the rules cannot happen — and comparing them would cost a walk of two
     * arrays on every entity on every frame for an answer already known.
     */
    case 'graph-marks':
      return (
        b.kind === 'graph-marks' &&
        a.points.length === b.points.length &&
        a.points.every((point, index) => samePoint(point, b.points[index] ?? point)) &&
        a.marks.length === b.marks.length &&
        a.marks.every((mark, index) => mark.text === b.marks[index]?.text)
      );

    case 'variation-table':
      return (
        b.kind === 'variation-table' &&
        samePoint(a.corner, b.corner) &&
        a.width === b.width &&
        a.height === b.height &&
        a.rules.length === b.rules.length &&
        a.marks.length === b.marks.length &&
        a.marks.every((mark, index) => mark.text === b.marks[index]?.text)
      );

    case 'function-graph':
      return (
        b.kind === 'function-graph' &&
        a.expression === b.expression &&
        a.segments.length === b.segments.length
      );

    case 'angle-mark':
      return (
        b.kind === 'angle-mark' &&
        samePoint(a.vertex, b.vertex) &&
        samePoint(a.from, b.from) &&
        samePoint(a.to, b.to) &&
        a.ticks === b.ticks &&
        a.size === b.size &&
        a.showValue === b.showValue &&
        // Squareness has to be compared: an angle can cross 90° without any vertex moving
        // far enough for the point comparison to notice, and the mark changes shape.
        a.isRight === b.isRight
      );

    /*
     * A solid compares by its edge lists. Rotating it by a degree changes every vertex, so
     * there is no cheaper summary that would not miss a real change and leave the canvas
     * showing the previous orientation.
     */
    // Two frames are the same when they put the same four points on the page; nothing else
    // about a viewpoint can differ.
    case 'space':
      return (
        b.kind === 'space' &&
        samePoint(a.frame.origin, b.frame.origin) &&
        samePoint(a.frame.x, b.frame.x) &&
        samePoint(a.frame.y, b.frame.y) &&
        samePoint(a.frame.z, b.frame.z)
      );

    case 'solid':
      return (
        b.kind === 'solid' &&
        samePoint(a.origin, b.origin) &&
        sameSegments(a.visible, b.visible) &&
        sameSegments(a.hidden, b.hidden)
      );

    case 'point':
      return b.kind === 'point' && samePoint(a.point, b.point);
    case 'segment':
      return (
        b.kind === 'segment' &&
        samePoint(a.segment.a, b.segment.a) &&
        samePoint(a.segment.b, b.segment.b)
      );
    case 'vector':
      return b.kind === 'vector' && samePoint(a.from, b.from) && samePoint(a.to, b.to);
    case 'ray':
      return (
        b.kind === 'ray' &&
        samePoint(a.ray.origin, b.ray.origin) &&
        samePoint(a.ray.direction, b.ray.direction)
      );
    case 'line':
      return (
        b.kind === 'line' &&
        samePoint(a.line.origin, b.line.origin) &&
        samePoint(a.line.direction, b.line.direction)
      );
    case 'circle':
      return (
        b.kind === 'circle' &&
        samePoint(a.circle.center, b.circle.center) &&
        a.circle.radius === b.circle.radius
      );
    case 'arc':
      return (
        b.kind === 'arc' &&
        samePoint(a.arc.center, b.arc.center) &&
        a.arc.radius === b.arc.radius &&
        a.arc.startAngle === b.arc.startAngle &&
        a.arc.endAngle === b.arc.endAngle &&
        a.arc.counterclockwise === b.arc.counterclockwise
      );
    case 'ellipse':
      return (
        b.kind === 'ellipse' &&
        samePoint(a.ellipse.center, b.ellipse.center) &&
        a.ellipse.rx === b.ellipse.rx &&
        a.ellipse.ry === b.ellipse.ry &&
        a.ellipse.rotation === b.ellipse.rotation
      );
    case 'polygon':
      return b.kind === 'polygon' && samePointList(a.vertices, b.vertices);
    case 'polyline':
      return b.kind === 'polyline' && a.closed === b.closed && samePointList(a.points, b.points);
    case 'text':
      return (
        b.kind === 'text' &&
        samePoint(a.position, b.position) &&
        a.content === b.content &&
        a.latex === b.latex
      );
    case 'measurement': {
      if (b.kind !== 'measurement' || !samePoint(a.position, b.position)) return false;
      if (a.value.type !== b.value.type) return false;
      if (a.value.type === 'scalar' && b.value.type === 'scalar') {
        return a.value.value === b.value.value && a.value.unit === b.value.unit;
      }
      if (a.value.type !== 'scalar' && b.value.type !== 'scalar') {
        return samePoint(a.value.value, b.value.value);
      }
      return false;
    }
    case 'image':
      return (
        b.kind === 'image' &&
        samePoint(a.position, b.position) &&
        a.width === b.width &&
        a.height === b.height &&
        a.href === b.href
      );
  }
}

function samePoint(a: { x: number; y: number }, b: { x: number; y: number }): boolean {
  return a.x === b.x && a.y === b.y;
}

function samePointList(
  a: readonly { x: number; y: number }[],
  b: readonly { x: number; y: number }[]
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];
    if (left === undefined || right === undefined) return false;
    if (!samePoint(left, right)) return false;
  }
  return true;
}

function sameSegments(
  a: readonly { a: { x: number; y: number }; b: { x: number; y: number } }[],
  b: readonly { a: { x: number; y: number }; b: { x: number; y: number } }[]
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];
    if (left === undefined || right === undefined) return false;
    if (!samePoint(left.a, right.a) || !samePoint(left.b, right.b)) return false;
  }
  return true;
}
